import { useState } from "react";
import api from "../../services/api";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import "./SalesReport.css";

export default function TaxReport() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleGenerateReport = async () => {
    if (!fromDate || !toDate) {
      return Swal.fire("Notice", "You must select both dates", "warning");
    }

    if (new Date(fromDate) > new Date(toDate)) {
      return Swal.fire("Error", "The start date cannot be later than the end date", "error");
    }

    setLoading(true);
    try {
      const response = await api.get("/reports/tax", {
        params: { from: fromDate, to: toDate },
      });

      setReportData(response.data);

      Swal.fire({
        icon: "success",
        title: "Report Generated",
        text: "The sales tax report was generated successfully",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error generating report:", error);
      Swal.fire("Error", "Could not generate the sales tax report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFromDate("");
    setToDate("");
    setReportData(null);
  };

  const handleExportExcel = () => {
    if (!reportData) return;

    const workbook = XLSX.utils.book_new();

    const summaryData = [
      ["SALES TAX REPORT"],
      ["Business:", reportData.company.nombre],
      ["State:", reportData.company.state || "-"],
      ["Tax Rate:", `${(reportData.company.sales_tax_rate * 100).toFixed(2)}%`],
      ["Period:", `${reportData.periodo.desde} to ${reportData.periodo.hasta}`],
      [],
      ["SUMMARY"],
      ["Total Transactions", reportData.resumen.total_transacciones],
      ["Total Gross Sales", `$${reportData.resumen.total_ventas_brutas.toFixed(2)}`],
      ["Taxable Sales", `$${reportData.resumen.total_gravado.toFixed(2)}`],
      ["Exempt Sales", `$${reportData.resumen.total_exento.toFixed(2)}`],
      ["Sales Tax Collected", `$${reportData.resumen.sales_tax_recaudado.toFixed(2)}`],
      ["Total Collected", `$${reportData.resumen.total_cobrado.toFixed(2)}`],
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, wsSummary, "Summary");

    const byDay = reportData.por_dia.map((day) => ({
      Date: day.fecha,
      Transactions: day.total_transacciones,
      "Gross Sales": day.total_ventas_brutas,
      Taxable: day.total_gravado,
      Exempt: day.total_exento,
      "Sales Tax": day.sales_tax_recaudado,
      Total: day.total_cobrado,
    }));
    const wsByDay = XLSX.utils.json_to_sheet(byDay);
    XLSX.utils.book_append_sheet(workbook, wsByDay, "By Day");

    const fileName = `Sales_Tax_Report_${fromDate}_to_${toDate}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    Swal.fire({
      icon: "success",
      title: "Excel Downloaded",
      text: "Give this file to your accountant for tax filing",
      timer: 2500,
      showConfirmButton: false,
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  return (
    <div className="sales-report-container">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">🧾 Sales Tax Report</h2>
        </div>

        <div className="card-body">
          <div className="filters-section">
            <div className="filters-row">
              <div className="form-group">
                <label className="form-label">📅 Start Date</label>
                <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </div>

              <div className="form-group">
                <label className="form-label">📅 End Date</label>
                <input type="date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </div>

              <div className="filters-actions">
                <button className="btn btn-primary" onClick={handleGenerateReport} disabled={loading}>
                  {loading ? "⏳ Generating..." : "🔍 Generate Report"}
                </button>
                <button className="btn btn-secondary" onClick={handleClear}>
                  🔄 Clear
                </button>
              </div>
            </div>
          </div>

          {loading && (
            <div className="text-center mt-4">
              <div className="spinner"></div>
              <p className="mt-2">Generating sales tax report...</p>
            </div>
          )}

          {reportData && !loading && (
            <div className="report-results">
              <div className="export-section">
                <button className="btn btn-success" onClick={handleExportExcel}>
                  📊 Export to Excel (for your accountant)
                </button>
              </div>

              <div className="summary-section">
                <h3 className="section-title">
                  📈 Summary — {reportData.company.nombre}
                  {reportData.company.state && ` (${reportData.company.state})`}
                </h3>
                <p style={{ color: "#718096", marginTop: "-10px", marginBottom: "20px" }}>
                  Tax rate applied: <strong>{(reportData.company.sales_tax_rate * 100).toFixed(2)}%</strong>
                </p>
                <div className="kpi-cards">
                  <div className="kpi-card">
                    <div className="kpi-icon">💰</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Total Gross Sales</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.total_ventas_brutas)}</div>
                    </div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-icon">✅</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Taxable Sales</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.total_gravado)}</div>
                    </div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-icon">🚫</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Exempt Sales</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.total_exento)}</div>
                    </div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-icon">🧾</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Sales Tax Collected</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.sales_tax_recaudado)}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="sales-by-day-section">
                <h3 className="section-title">📅 Breakdown by Day</h3>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Transactions</th>
                        <th>Gross Sales</th>
                        <th>Taxable</th>
                        <th>Exempt</th>
                        <th>Sales Tax</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.por_dia.map((day, index) => (
                        <tr key={index}>
                          <td>{new Date(day.fecha).toLocaleDateString("en-US")}</td>
                          <td className="text-center">{day.total_transacciones}</td>
                          <td className="text-right">{formatCurrency(day.total_ventas_brutas)}</td>
                          <td className="text-right">{formatCurrency(day.total_gravado)}</td>
                          <td className="text-right">{formatCurrency(day.total_exento)}</td>
                          <td className="text-right">{formatCurrency(day.sales_tax_recaudado)}</td>
                          <td className="text-right font-bold">{formatCurrency(day.total_cobrado)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="total-row">
                        <td><strong>TOTAL</strong></td>
                        <td className="text-center"><strong>{reportData.resumen.total_transacciones}</strong></td>
                        <td className="text-right"><strong>{formatCurrency(reportData.resumen.total_ventas_brutas)}</strong></td>
                        <td className="text-right"><strong>{formatCurrency(reportData.resumen.total_gravado)}</strong></td>
                        <td className="text-right"><strong>{formatCurrency(reportData.resumen.total_exento)}</strong></td>
                        <td className="text-right"><strong>{formatCurrency(reportData.resumen.sales_tax_recaudado)}</strong></td>
                        <td className="text-right"><strong>{formatCurrency(reportData.resumen.total_cobrado)}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}

          {!reportData && !loading && (
            <div className="no-data">
              <p>📭 Select a date range to generate your sales tax report</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}